// Lightweight skeleton loaders - shimmer animation, theme-matched
import './Skeleton.css'

export function PosterSkeleton({ count = 6, columns = 3, className, style }) {
  return (
    <div className={className} style={{ display:'grid', gridTemplateColumns:`repeat(${columns}, 1fr)`, gap:10, padding:'12px 16px', ...style }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="skel" style={{
          aspectRatio:'2/3', borderRadius:'var(--radius-sm)',
          animationDelay: `${i * 0.05}s`,
        }}/>
      ))}
    </div>
  )
}

export function RowSkeleton({ title, count = 6 }) {
  return (
    <div style={{ marginTop:18 }}>
      <div style={{ padding:'0 16px', marginBottom:8 }}>
        {title
          ? <div style={{ color:'var(--text)', fontSize:15, fontWeight:600 }}>{title}</div>
          : <div className="skel" style={{ height:14, width:120, borderRadius:4 }}/>}
      </div>
      <div style={{ display:'flex', gap:10, overflowX:'hidden', padding:'0 16px 6px' }}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="skel" style={{
            flexShrink:0, width:104, aspectRatio:'2/3',
            borderRadius:'var(--radius-sm)',
            animationDelay: `${i * 0.05}s`,
          }}/>
        ))}
      </div>
    </div>
  )
}

export function ListItemSkeleton({ count = 5 }) {
  return (
    <div style={{ padding:'12px 16px', display:'flex', flexDirection:'column', gap:10 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ display:'flex', gap:11, alignItems:'center', padding:'10px', borderRadius:'var(--radius)' }}>
          <div className="skel" style={{ width:46, height:70, borderRadius:6, animationDelay:`${i*0.06}s` }}/>
          <div style={{ flex:1, display:'flex', flexDirection:'column', gap:6 }}>
            <div className="skel" style={{ height:13, width:'70%', borderRadius:3, animationDelay:`${i*0.06+0.02}s` }}/>
            <div className="skel" style={{ height:10, width:'40%', borderRadius:3, animationDelay:`${i*0.06+0.04}s` }}/>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── ShowDetail page skeleton — hero + poster + 3 buttons + episode picker ─
export function ShowDetailSkeleton() {
  return (
    <div style={{ minHeight:'100dvh', background:'var(--bg)' }}>
      {/* Hero backdrop area */}
      <div className="skel" style={{ width:'100%', height:280, marginBottom:-90 }}/>

      {/* Poster + title block */}
      <div style={{ display:'flex', gap:14, padding:'0 16px', position:'relative', zIndex:1 }}>
        <div className="skel" style={{ width:95, height:142, borderRadius:'var(--radius)', flexShrink:0 }}/>
        <div style={{ flex:1, paddingTop:60, display:'flex', flexDirection:'column', gap:8 }}>
          <div className="skel" style={{ height:22, width:'80%', borderRadius:4 }}/>
          <div style={{ display:'flex', gap:6, marginTop:6 }}>
            <div className="skel" style={{ height:22, width:60, borderRadius:11 }}/>
            <div className="skel" style={{ height:22, width:90, borderRadius:11 }}/>
            <div className="skel" style={{ height:22, width:50, borderRadius:11 }}/>
          </div>
        </div>
      </div>

      {/* 3-button action row */}
      <div style={{ display:'flex', gap:8, padding:'18px 16px 0' }}>
        <div className="skel" style={{ flex:1, height:42, borderRadius:'var(--radius)' }}/>
        <div className="skel" style={{ flex:1, height:42, borderRadius:'var(--radius)' }}/>
        <div className="skel" style={{ flex:1, height:42, borderRadius:'var(--radius)' }}/>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:24, padding:'24px 16px 12px' }}>
        <div className="skel" style={{ height:16, width:60, borderRadius:3 }}/>
        <div className="skel" style={{ height:16, width:70, borderRadius:3 }}/>
        <div className="skel" style={{ height:16, width:50, borderRadius:3 }}/>
        <div className="skel" style={{ height:16, width:60, borderRadius:3 }}/>
      </div>

      {/* Description lines */}
      <div style={{ padding:'12px 16px', display:'flex', flexDirection:'column', gap:8 }}>
        <div className="skel" style={{ height:13, width:'100%', borderRadius:3 }}/>
        <div className="skel" style={{ height:13, width:'94%',  borderRadius:3 }}/>
        <div className="skel" style={{ height:13, width:'78%',  borderRadius:3 }}/>
      </div>
    </div>
  )
}

// ── Notification row skeleton (icon + 2 lines of text) ─
export function NotificationListSkeleton({ count = 4 }) {
  return (
    <div style={{ padding:'0 16px', display:'flex', flexDirection:'column', gap:10 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ display:'flex', gap:11, alignItems:'center', padding:'12px',
          background:'var(--bg-card)', borderRadius:'var(--radius)', border:'1px solid var(--border-soft)' }}>
          <div className="skel" style={{ width:38, height:38, borderRadius:'var(--radius-full)', flexShrink:0, animationDelay:`${i*0.06}s` }}/>
          <div style={{ flex:1, display:'flex', flexDirection:'column', gap:6 }}>
            <div className="skel" style={{ height:13, width:'75%', borderRadius:3, animationDelay:`${i*0.06+0.02}s` }}/>
            <div className="skel" style={{ height:10, width:'45%', borderRadius:3, animationDelay:`${i*0.06+0.04}s` }}/>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Home deck skeleton — matches HomeDeck's real card shape (poster + title
// line + sub-line + 3-button action row) so a cold boot never flashes "Nothing
// here yet." before the watchlist has actually loaded (2026-09-17: HomeDeck
// never gated on wl.ready, so an empty-but-not-yet-fetched list briefly read as
// a genuinely empty one). Sized off the same 390×500-on-430-canvas ratio the
// real deck card uses. ─
export function HomeDeckSkeleton() {
  return (
    <div style={{ padding: '0 20px' }}>
      <div className="skel" style={{ width: '100%', aspectRatio: '390 / 500', borderRadius: 'var(--radius-lg, 20px)' }} />
      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div className="skel" style={{ height: 24, width: '55%', borderRadius: 4 }} />
        <div className="skel" style={{ height: 14, width: '35%', borderRadius: 3 }} />
      </div>
      <div style={{ display: 'flex', gap: 24, marginTop: 20 }}>
        <div className="skel" style={{ height: 34, width: 34, borderRadius: '50%' }} />
        <div className="skel" style={{ height: 34, width: 34, borderRadius: '50%' }} />
        <div className="skel" style={{ height: 34, width: 34, borderRadius: '50%' }} />
      </div>
    </div>
  )
}

// ── Calendar-row schedule skeleton ─
export function ScheduleSkeleton({ count = 4 }) {
  return (
    <div style={{ padding:'0 16px', display:'flex', flexDirection:'column', gap:18 }}>
      {Array.from({ length: 2 }).map((_, dayIdx) => (
        <div key={dayIdx}>
          <div className="skel" style={{ height:11, width:120, borderRadius:3, marginBottom:8 }}/>
          {Array.from({ length: count / 2 }).map((_, i) => (
            <div key={i} style={{ display:'flex', gap:12, alignItems:'center', padding:'10px 12px',
              background:'var(--bg-card)', borderRadius:'var(--radius)', marginBottom:8 }}>
              <div className="skel" style={{ width:36, height:54, borderRadius:6, flexShrink:0 }}/>
              <div style={{ flex:1, display:'flex', flexDirection:'column', gap:6 }}>
                <div className="skel" style={{ height:13, width:'60%', borderRadius:3 }}/>
                <div className="skel" style={{ height:10, width:'40%', borderRadius:3 }}/>
              </div>
              <div className="skel" style={{ width:34, height:34, borderRadius:'var(--radius-xs)', flexShrink:0 }}/>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

